// Geometrie der Flurskizze zum Ziehen (30.10.2026), ohne Maßstab. Abschnitt 1 beginnt unten am Eingang und führt nach
// oben. Seit 31.10.2026 ein Baum (Betreiber: „das ultimative Flurwerkzeug“): Jeder Gang kann am Ende abknicken (links,
// rechts oder beides = T) und unterwegs Abzweige haben (beide Seiten an derselben Stelle = Kreuzung); jeder neue Gang
// kann dasselbe. Eine Diele ist ein Quadrat, von dessen Wänden Gänge abgehen können. Die Länge eines Gangs mit Ecke ist
// ziehbar (laenge), die Wohnungstür immer. Wände: <Abschnitt>:links|rechts|ende|anfang, Lage 0..1 in Gehrichtung
// (Stirnwand: von links nach rechts).

import {
  HALL_LENGTH_MAX,
  HALL_LENGTH_MIN,
  branchChildren,
  endChildren,
  normalizeSections,
  sectionOrder,
  type HallSection,
  type HallShape,
  type HallSide,
} from "@umzugsscan/shared";
import type { Pt, Wand } from "./ui/ZiehSkizze.tsx";

const DIRS: Pt[] = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
];
const LEN = 230;
const BREIT = 64;
const DIELE = 220;

const add = (a: Pt, b: Pt, k = 1): Pt => ({ x: a.x + b.x * k, y: a.y + b.y * k });
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Ziehbare Ecke: Ende eines Gangs mit Ecke(n), verschiebbar entlang seiner Achse (setzt laenge bzw. kurz). */
export type LaengenGriff = { nr: number; wall: Wand; pos: number; min: number; max: number; base: number };

export type FlurBild = {
  walls: Wand[];
  rects: { x: number; y: number; w: number; h: number }[];
  /** Nummer des Abschnitts in der Mitte des Gangs (nur bei mehreren); quer = Richtung quer zum Gang (Nummer und „−“ nebeneinander). */
  heads: { nr: number; p: Pt; quer: Pt }[];
  entrance: Pt;
  /** Richtung in den Flur hinein (vom Eingang aus). */
  entranceDir: Pt;
  /** Wände, an denen die Wohnungstür liegen kann (e:anfang, e:links, e:rechts). */
  eingangWaende: Wand[];
  eingang: { seite: "anfang" | "links" | "rechts"; pos: number };
  /** Wand, an der ein Abzweig bzw. ein Gang aus der Diele sitzt (für den ziehbaren Punkt ↔). */
  abzweigWand: Map<number, Wand>;
  laengen: LaengenGriff[];
  /** „+“ je Gang (31.10.2026): am Ende, solange der Gang dort endet, sonst in seiner Mitte (für Abzweige). */
  plus: { nr: number; p: Pt; amEnde: boolean }[];
  /** „+“ an den Wänden der Diele: ein Gang geht nach links, geradeaus oder rechts ab. */
  dielePlus: { richtung: "links" | "geradeaus" | "rechts"; p: Pt }[];
  box: { x: number; y: number; w: number; h: number };
};

export const wallId = (section: number, side: HallSide) => `${section}:${side}`;
export function parseWall(id: string): { section: number; side: HallSide } {
  const [s, side] = id.split(":");
  return { section: Number(s), side: side as HallSide };
}

/** Standardlänge eines Gangs: Abzweige etwas kürzer. */
const baseLen = (s: HallSection) => (s.art === "abzweig" ? LEN * 0.75 : LEN);
/** Länge eines Gangs; „kurz“ am Start = Wohnungstür direkt an der Ecke. */
export const lenOf = (s: HallSection) => (s.art === "start" && s.kurz ? BREIT / 2 : baseLen(s) * clamp(s.laenge ?? 1, HALL_LENGTH_MIN, HALL_LENGTH_MAX));

/** Abzweig „gleich an der Wohnungstür“: Stelle (bei) so, dass der Abzweig bündig an der Eingangswand beginnt. */
export const beiAnDerTuer = (parent: HallSection) => (BREIT / 2 + 1) / (lenOf(parent) + (parent.art === "start" ? BREIT / 2 : BREIT));

/** Gezogene Lage der Ecke (0..1 am Längen-Griff) in Angaben am Abschnitt umrechnen. */
export function laengeAus(g: LaengenGriff, pos: number, start: boolean): Partial<HallSection> {
  const len = g.min + (g.max - g.min) * clamp(pos, 0, 1);
  if (start && len < g.base * 0.22) return { kurz: true, laenge: null };
  return { kurz: false, laenge: Math.round(clamp(len / g.base, HALL_LENGTH_MIN, HALL_LENGTH_MAX) * 100) / 100 };
}

export function flurBild(sectionsIn: HallSection[] | null | undefined, shape: HallShape | null | undefined): FlurBild {
  const walls: Wand[] = [];
  const rects: FlurBild["rects"] = [];
  const heads: FlurBild["heads"] = [];
  const pts: Pt[] = [];
  const abzweigWand = new Map<number, Wand>();
  const laengen: LaengenGriff[] = [];
  const plus: FlurBild["plus"] = [];
  const dielePlus: FlurBild["dielePlus"] = [];
  const sections = normalizeSections(sectionsIn);
  const placed = new Set<number>();

  const place = (s: HallSection, start: Pt, dir: number) => {
    if (placed.has(s.nr)) return;
    placed.add(s.nr);
    const len = lenOf(s);
    const d = DIRS[dir]!;
    const nl = DIRS[(dir + 3) % 4]!;
    const nr = DIRS[(dir + 1) % 4]!;
    const e = add(start, d, len);
    const a = add(start, d, s.art === "start" ? 0 : -BREIT / 2);
    const b = add(e, d, BREIT / 2);
    const x0 = Math.min(a.x, b.x) - (d.x === 0 ? BREIT / 2 : 0);
    const y0 = Math.min(a.y, b.y) - (d.y === 0 ? BREIT / 2 : 0);
    const w = d.x === 0 ? BREIT : Math.abs(b.x - a.x);
    const h = d.y === 0 ? BREIT : Math.abs(b.y - a.y);
    rects.push({ x: x0, y: y0, w, h });
    pts.push({ x: x0, y: y0 }, { x: x0 + w, y: y0 + h });
    const lead = s.art === "start" ? 0 : BREIT / 2;
    // Seitenwände über die ganze Länge des Gangs, auch durch Ecken und Übergänge: dort können Türen sein (Betreiber 30.10.2026).
    const links: Wand = { id: wallId(s.nr, "links"), a: add(a, nl, BREIT / 2), b: add(b, nl, BREIT / 2), out: nl };
    const rechts: Wand = { id: wallId(s.nr, "rechts"), a: add(a, nr, BREIT / 2), b: add(b, nr, BREIT / 2), out: nr };
    walls.push(links, rechts, { id: wallId(s.nr, "ende"), a: add(b, nl, BREIT / 2), b: add(b, nr, BREIT / 2), out: d });
    // Nummer im Gang; im ersten Gang über dem Pfeil der Wohnungstür.
    if (sections.length > 1) heads.push({ nr: s.nr, p: add(start, d, s.art === "start" ? Math.max(22, Math.min(len - 20, 80)) : lead + 22), quer: nr });
    for (const br of branchChildren(sections, s.nr).filter((x) => x.richtung)) {
      // bei = Anteil der ganzen Seitenwand des Ausgangsabschnitts (wie der Punkt ↔ in der Skizze), 30.10.2026.
      const from = s.art === "start" ? 0 : -BREIT / 2;
      const full = len + (s.art === "start" ? BREIT / 2 : BREIT);
      const at = add(start, d, from + full * clamp(br.bei ?? 0.5, 0.03, 0.97));
      const side = br.richtung === "links" ? (dir + 3) % 4 : (dir + 1) % 4;
      abzweigWand.set(br.nr, br.richtung === "links" ? links : rechts);
      place(br, add(at, DIRS[side]!, BREIT / 2), side);
    }
    const ecken = endChildren(sections, s.nr).filter((x) => x.richtung === "links" || x.richtung === "rechts");
    if (ecken.length > 0) {
      // Ecke verschiebbar: Griff am Ende des Gangs, entlang seiner Achse.
      const min = s.art === "start" ? BREIT / 2 : baseLen(s) * HALL_LENGTH_MIN;
      const max = baseLen(s) * HALL_LENGTH_MAX;
      const wall: Wand = { id: `laenge:${s.nr}`, a: add(start, d, min), b: add(start, d, max), out: nr };
      laengen.push({ nr: s.nr, wall, pos: (len - min) / (max - min), min, max, base: baseLen(s) });
    }
    // „+“: am Ende des Gangs, wenn er dort endet; sonst in der Mitte (dort lassen sich noch Abzweige anlegen).
    if (ecken.length === 0) plus.push({ nr: s.nr, p: e, amEnde: true });
    else if (!(s.art === "start" && s.kurz)) plus.push({ nr: s.nr, p: add(start, d, lead + Math.max(48, len * 0.55)), amEnde: false });
    for (const c of ecken) place(c, e, c.richtung === "links" ? (dir + 3) % 4 : (dir + 1) % 4);
  };

  if (shape === "diele") {
    const h = DIELE / 2;
    rects.push({ x: -h, y: -DIELE, w: DIELE, h: DIELE });
    const wl: Wand = { id: wallId(1, "links"), a: { x: -h, y: 0 }, b: { x: -h, y: -DIELE }, out: { x: -1, y: 0 } };
    const we: Wand = { id: wallId(1, "ende"), a: { x: -h, y: -DIELE }, b: { x: h, y: -DIELE }, out: { x: 0, y: -1 } };
    const wr: Wand = { id: wallId(1, "rechts"), a: { x: h, y: 0 }, b: { x: h, y: -DIELE }, out: { x: 1, y: 0 } };
    walls.push(wl, we, wr);
    pts.push({ x: -h, y: -DIELE }, { x: h, y: 0 });
    placed.add(1);
    // „+“ nur an Wänden, von denen noch kein Gang abgeht.
    const frei = (r: string) => !sections.some((x) => x.von === 1 && x.nr !== 1 && x.richtung === r);
    if (frei("links")) dielePlus.push({ richtung: "links", p: { x: -h + 30, y: -DIELE / 2 } });
    if (frei("geradeaus")) dielePlus.push({ richtung: "geradeaus", p: { x: 0, y: -DIELE + 30 } });
    if (frei("rechts")) dielePlus.push({ richtung: "rechts", p: { x: h - 30, y: -DIELE / 2 } });
    // Gänge aus der Diele (31.10.2026): an der linken, hinteren oder rechten Wand, Stelle ziehbar.
    for (const g of sections.filter((x) => x.von === 1 && x.nr !== 1 && x.richtung)) {
      const t = clamp(g.bei ?? 0.5, 0.12, 0.88);
      const [wand, dir] = g.richtung === "links" ? [wl, 3] : g.richtung === "rechts" ? [wr, 1] : [we, 0];
      abzweigWand.set(g.nr, wand);
      place(g, { x: wand.a.x + (wand.b.x - wand.a.x) * t, y: wand.a.y + (wand.b.y - wand.a.y) * t }, dir);
    }
  } else {
    place(sections[0]!, { x: 0, y: 0 }, 0);
  }
  // Wohnungstür: unten an der Stirnseite (Standard) oder seitlich an Abschnitt 1, vom Kunden verschiebbar – auch bei der Diele.
  const len1 = shape === "diele" ? DIELE : lenOf(sections[0]!);
  const halb = shape === "diele" ? DIELE / 2 : BREIT / 2;
  const eingangWaende: Wand[] = [
    { id: "e:anfang", a: { x: -halb, y: 0 }, b: { x: halb, y: 0 }, out: { x: 0, y: 1 } },
    { id: "e:links", a: { x: -halb, y: 0 }, b: { x: -halb, y: -len1 }, out: { x: -1, y: 0 } },
    { id: "e:rechts", a: { x: halb, y: 0 }, b: { x: halb, y: -len1 }, out: { x: 1, y: 0 } },
  ];
  // Türen auch an der Eingangswand selbst (30.10.2026): neben der Wohnungstür oder, wenn diese seitlich liegt, dort.
  walls.push({ id: wallId(1, "anfang"), a: { x: -halb, y: 0 }, b: { x: halb, y: 0 }, out: { x: 0, y: 1 } });
  const e = sections[0]!.eingang ?? { seite: "anfang" as const, pos: 0.5 };
  const ew = eingangWaende.find((w) => w.id === `e:${e.seite}`)!;
  const entrance = { x: ew.a.x + (ew.b.x - ew.a.x) * e.pos, y: ew.a.y + (ew.b.y - ew.a.y) * e.pos };
  const entranceDir = { x: -ew.out.x, y: -ew.out.y };
  pts.push({ x: -40, y: 44 }, { x: 40, y: 0 });
  // Platz für die Beschriftung außen an den Wänden.
  const rand = 150;
  const minX = Math.min(...pts.map((p) => p.x)) - rand;
  const minY = Math.min(...pts.map((p) => p.y)) - 60;
  const maxX = Math.max(...pts.map((p) => p.x)) + rand;
  const maxY = Math.max(...pts.map((p) => p.y)) + 30;
  return { walls, rects, heads, entrance, entranceDir, eingangWaende, eingang: e, abzweigWand, laengen, plus, dielePlus, box: { x: minX, y: minY, w: maxX - minX, h: maxY - minY } };
}

/** Reihenfolge der Abschnitte entlang des Wegs: Gang, um seine Ecken weiter, dann seine Abzweige (sectionOrder). */
export function sectionRank(sectionsIn: HallSection[] | null | undefined): Map<number, number> {
  return new Map(sectionOrder(sectionsIn).map((s, i) => [s.nr, i]));
}

/** Sortierschlüssel einer Tür: Abschnitt, dann Lage (Eingangswand zuerst, Stirnwand zuletzt), links vor rechts. */
export function doorKey(rank: Map<number, number>, section: number | null | undefined, side: HallSide | null | undefined, pos: number | null | undefined): number {
  const r = rank.get(section ?? 1) ?? 99;
  const p = side === "ende" ? 1.5 + (pos ?? 0.5) / 10 : side === "anfang" ? -1 + (pos ?? 0.5) / 10 : (pos ?? 0.5);
  return r * 10 + p + (side === "rechts" ? 0.0001 : 0);
}
